// ============= Imports =============
import React, { useState } from 'react';
import { Button, Modal } from '@/ui';
import { CREATE_PROMPT_MESSAGES } from '@/constants/visualPrompts/createPromptMessages';
import { PROMPT_CATEGORY_FILTERS } from '@/constants/visualPrompts/promptCategories';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { buildCustomVisualPromptDefinition } from '@/utils/visualPrompts/buildCustomVisualPromptDefinition';
import { saveCustomVisualPrompt } from '@/utils/visualPrompts/customVisualPromptStorage';
import type { VisualPromptCategory } from '@/types/visualPrompts/visualPrompt.types';
import {
  ErrorText,
  Field,
  Footer,
  Form,
  Input,
  Intro,
  Pill,
  PillRow,
  TextArea,
} from './styles';

const CATEGORY_OPTIONS = PROMPT_CATEGORY_FILTERS.filter((c) => c.id !== 'all');

export interface CreateVisualPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// ============= Component =============
export const CreateVisualPromptModal: React.FC<CreateVisualPromptModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const { handleSuccess, handleError } = useWorkspaceErrorHandler();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<VisualPromptCategory>('general');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setCategory('general');
    setFieldError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    if (!title.trim()) {
      setFieldError(CREATE_PROMPT_MESSAGES.titleRequired);
      return;
    }
    if (!description.trim()) {
      setFieldError(CREATE_PROMPT_MESSAGES.descriptionRequired);
      return;
    }
    setFieldError(null);
    setIsSaving(true);
    try {
      const definition = buildCustomVisualPromptDefinition({
        title,
        description,
        category,
      });
      saveCustomVisualPrompt(definition);
      handleSuccess(CREATE_PROMPT_MESSAGES.saveSuccess);
      reset();
      onSaved();
      onClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[CreateVisualPromptModal] Save Failed:', error);
      handleError(error, CREATE_PROMPT_MESSAGES.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={CREATE_PROMPT_MESSAGES.modalTitle}
      size="medium"
      footer={
        <Footer>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            {CREATE_PROMPT_MESSAGES.cancel}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {CREATE_PROMPT_MESSAGES.save}
          </Button>
        </Footer>
      }
    >
      <Form>
        <Intro>{CREATE_PROMPT_MESSAGES.modalDescription}</Intro>
        <Field>
          {CREATE_PROMPT_MESSAGES.fieldTitle}
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={CREATE_PROMPT_MESSAGES.fieldTitlePlaceholder}
          />
        </Field>
        <Field>
          {CREATE_PROMPT_MESSAGES.fieldDescription}
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={CREATE_PROMPT_MESSAGES.fieldDescriptionPlaceholder}
          />
        </Field>
        <Field>
          {CREATE_PROMPT_MESSAGES.fieldCategory}
          <PillRow>
            {CATEGORY_OPTIONS.map((opt) => (
              <Pill
                key={opt.id}
                type="button"
                $active={category === opt.id}
                onClick={() => setCategory(opt.id as VisualPromptCategory)}
              >
                {opt.label}
              </Pill>
            ))}
          </PillRow>
        </Field>
        {fieldError && <ErrorText>{fieldError}</ErrorText>}
      </Form>
    </Modal>
  );
};
